import {
  addDays,
  format,
  getISOWeek,
  getISOWeekYear,
  isValid,
  parse,
  parseISO,
  set,
  startOfISOWeek,
} from "date-fns"

const localDateTimeFormats = [
  "yyyy-MM-dd'T'HH:mm:ss.SSS",
  "yyyy-MM-dd'T'HH:mm:ss.SS",
  "yyyy-MM-dd'T'HH:mm:ss.S",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
] as const

export const formatDateInput = (value: Date) => {
  return format(value, "yyyy-MM-dd'T'HH:mm")
}

const parseLocalDateTime = (value: string) => {
  for (const dateFormat of localDateTimeFormats) {
    const parsed = parse(value, dateFormat, new Date())
    if (!isValid(parsed)) {
      continue
    }

    if (format(parsed, dateFormat) !== value) {
      continue
    }

    return parsed
  }

  return null
}

export const parseDateInput = (value: string) => {
  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return null
  }

  const parsedLocalDateTime = parseLocalDateTime(trimmedValue)
  if (parsedLocalDateTime) {
    return parsedLocalDateTime
  }

  const parsedIso = parseISO(trimmedValue)
  if (isValid(parsedIso)) {
    return parsedIso
  }

  const fallbackDate = new Date(trimmedValue)
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate
}

export const coerceDateLikeToString = (
  value: string | Date | null | undefined,
) => {
  if (value instanceof Date) {
    return value.toISOString()
  }

  return value ?? ""
}

export const coerceDateTimeLocalValue = (
  value: string | Date | null | undefined,
) => {
  const normalized = coerceDateLikeToString(value)
  if (!normalized) {
    return ""
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) {
    return normalized
  }

  return formatDateInput(parsed)
}

export const getIsoWeekId = (value: Date) => {
  const weekYear = getISOWeekYear(value)
  const week = getISOWeek(value)
  return `${weekYear}-W${String(week).padStart(2, "0")}`
}

export const getCurrentWeekWindow = (referenceDate = new Date()) => {
  const currentWeekMonday = startOfISOWeek(referenceDate)
  const weekStart = currentWeekMonday
  const weekEnd = set(addDays(weekStart, 6), {
    hours: 23,
    minutes: 59,
    seconds: 0,
    milliseconds: 0,
  })

  return { weekStart, weekEnd }
}
